import {
  AwardIcon,
  CalculatorIcon,
  ClockIcon,
  EnvelopeCheckIcon,
  FunnelIcon,
  GaugeIcon,
  HourglassIcon,
  MicIcon,
  MoneyIcon,
  PercentIcon,
  PieChartIcon,
  StopwatchIcon,
  UserCheckIcon,
  UserSearchIcon,
  UserXIcon,
  WalletIcon,
  FileIcon,
  GearIcon,
  DashboardIcon,
  DepartmentsBoardIcon,
  PersonIcon,
  BuildingIcon,
  StarIcon,
  TriangleIcon,
  ChartIcon,
  DatabaseIcon,
  FormIcon,
  CalendarIcon,
  OrgChartIcon,
  ChatIcon,
  OfferIcon,
  HierarchyIcon,
  AssessmentIcon,
  EmployeeIcon,
  TrainingHubIcon,
} from '../sidebar-styles'

const createCount = (
  count,
  hoverLabel,
  theme,
  className = 'rounded-circle mt-1 text-center',
) => ({
  value: count >= 100 ? '99+' : count,
  hoverText: `${hoverLabel}: ${count}`,
  borderColor: count === 0 ? theme.blue : theme.appleRed,
  backgroundColor: count === 0 ? theme.persianBlue : theme.faluRed,
  className,
})

export const createMenuStructure = (
  t,
  isAgency,
  organizationType,
  menuItemsCounter,
  theme,
  permissions,
  unitNamingLabels = {},
  isEmployee = false,
) => {
  const filterForEmployee = (section) => ({
    ...section,
    items: section.items.filter((item) => item.permission !== undefined),
  })
  const sections = [
    createDashboardSection(t),
    createRecruitmentSection(t, menuItemsCounter, theme, permissions),
    createPeopleSection(t, permissions),
    createOrganizationSection(
      t,
      organizationType,
      isAgency,
      permissions,
      unitNamingLabels,
    ),
    createAnalyticsSection(t, isAgency, permissions),
    createPlanningSection(t, permissions),
    createToolsSection(t, menuItemsCounter, theme, permissions),
    createSettingsSection(t, permissions),
  ]

  if (!isEmployee) return sections
  return sections
    .map(filterForEmployee)
    .filter((section) => section.items.length > 0)
}

const createDashboardSection = (t) => ({
  section: t('layout.dashboards'),
  items: [
    {
      icon: DashboardIcon,
      label: t('layout.dashboards'),
      href: '/',
    },
  ],
})

const createRecruitmentSection = (t, menuItemsCounter, theme, permissions) => ({
  section: t('layout.recruitment'),
  items: [
    createRequisitionsItem(t, menuItemsCounter, theme, permissions),
    createJobRequestItem(t, menuItemsCounter, theme, permissions),
    createPositionItem(t, permissions),
    createCandidatesItem(t, permissions),
    createAssessmentsItem(t, menuItemsCounter, theme),
    createEvaluationsItem(t, menuItemsCounter, theme, permissions),
    createOffersItem(t, menuItemsCounter, theme, permissions),
  ],
})

const createRequisitionsItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: StarIcon,
  label: t('general.requisitions_management'),
  href: '/requisitions',
  permission: permissions.requisitionsReadHistory,
  count: createCount(
    menuItemsCounter?.hiringRequests,
    'Pending',
    theme,
    'rounded-circle mt-1 text-center',
  ),
})

const createJobRequestItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: FileIcon,
  label: t('requisition.jobRequests'),
  href: '/job-requests',
  permission: permissions.jobRequestsRead,
  count: createCount(menuItemsCounter?.jobRequests, 'Pending', theme),
})

const createPositionItem = (t, permissions) => ({
  icon: TriangleIcon,
  label: t('general.positions'),
  href: '/positions',
  permission: permissions.positionRead,
})

const createCandidatesItem = (t, permissions) => ({
  icon: PersonIcon,
  label: t('general.candidates'),
  subItems: [
    {
      icon: PersonIcon,
      label: t('general.candidatesManagement'),
      href: '/local-candidates',
      permission: permissions.candidateReadAll,
    },
    {
      icon: PersonIcon,
      label: t('general.blacklistCandidates'),
      href: '/blacklist-candidates',
      permission: permissions.candidateReadBlacklisted,
    },
    {
      icon: PersonIcon,
      label: t('general.blockedCandidates'),
      href: '/blocked-candidates',
      permission: permissions.candidateReadBlacklisted,
    },
    {
      icon: PersonIcon,
      label: t('talentPool.talentPoolsCandidates'),
      href: '/talent-pools',
      permission: permissions.candidateReadAll,
    },
  ],
})

const createAssessmentsItem = (t, menuItemsCounter, theme) => ({
  icon: AssessmentIcon,
  label: t('assessment.assessments'),
  href: '/assessments',
  count: createCount(menuItemsCounter?.pendingAssessments, 'Pending', theme),
})

const createEvaluationsItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: FormIcon,
  label: t('layout.evaluations'),
  href: '/evaluation-forms',
  permission: permissions.evaluationFormsReadAll,
  count: createCount(menuItemsCounter?.pendingEvalForms, 'Pending', theme),
})

const createOffersItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: OfferIcon,
  label: t('pages.offers'),
  href: '/offers',
  permission: permissions.offerRead,
  count: createCount(menuItemsCounter?.pendingOffers, 'Unread', theme),
})

const createPeopleSection = (t, permissions) => ({
  section: t('layout.people'),
  items: [
    createEmployeesItem(t, permissions),
    createLearningItem(t, permissions),
  ],
})

const createEmployeesItem = (t, permissions) => ({
  icon: EmployeeIcon,
  label: t('dashboard.employees'),
  href: '/employees',
  permission: permissions.onboardingEmployeeRead,
})

const createLearningItem = (t, permissions) => ({
  icon: TrainingHubIcon,
  label: t('layout.learning'),
  permission: permissions.trainingAssignmentMyLearnings,
  subItems: [
    {
      icon: TrainingHubIcon,
      label: t('layout.trainingHub'),
      href: '/training-hub',
      permission: permissions.trainingCreate,
    },
    {
      icon: TrainingHubIcon,
      label: t('layout.myLearning'),
      href: '/my-learning',
      permission: permissions.trainingAssignmentMyLearnings,
    },
  ],
})

const createOrganizationSection = (
  t,
  organizationType,
  isAgency,
  permissions,
  unitNamingLabels = {},
) => ({
  section: t('layout.organization', { organizationType }),
  items: [
    createOrganizationChartItem(t, permissions),
    createOrganizationTableItem(t, permissions),
    createBranchesItem(t, unitNamingLabels),
    ...(isAgency
      ? [createClientsBoardItem(t, organizationType, permissions)]
      : []),
  ],
})

const createOrganizationChartItem = (t, permissions) => ({
  icon: OrgChartIcon,
  label: t('layout.organizationChartMenu'),
  href: '/workforce-structure/chart',
  permission: permissions.reportsSourcing,
})

const createOrganizationTableItem = (t, permissions) => ({
  icon: DatabaseIcon,
  label: t('layout.organizationTableMenu'),
  href: '/workforce-structure/table',
  permission: permissions.reportsSourcing,
})

const createBranchesItem = (t, unitNamingLabels = {}) => ({
  icon: BuildingIcon,
  label: unitNamingLabels.unitsPanel || t('layout.branches'),
  href: '/branches',
})

const createClientsBoardItem = (t, organizationType, permissions) => ({
  icon: DepartmentsBoardIcon,
  label: t('layout.organizationBoard', { organizationType }),
  href: '/organizations',
  permission: permissions.organizationReadAll,
})

const createAnalyticsSection = (t, isAgency, permissions) => ({
  section: t('layout.analytics'),
  items: [
    createRecruitmentAnalyticsItem(t, permissions),
    createHiringAnalyticsItem(t, isAgency, permissions),
    createRequisitionAnalyticsItem(t, permissions),
    createQualityOfHireItem(t, permissions),
    createPerformanceItem(t, permissions),
  ],
})

const createRecruitmentAnalyticsItem = (t, permissions) => ({
  icon: FunnelIcon,
  label: t('layout.recruitmentAnalytics'),
  permission: permissions.reportsSourcing,
  subItems: [
    {
      label: t('pages.sourcing'),
      href: '/reports/sourcing',
      permission: permissions.reportsSourcing,
    },
  ],
})

const createRequisitionAnalyticsItem = (t, permissions) => ({
  icon: ClockIcon,
  label: t('layout.requisitionAnalytics'),
  permission: permissions.reportsSourcing,
  subItems: [
    {
      label: t('reports.requisitionAging.menuLabel'),
      href: '/reports/requisition-aging',
      permission: permissions.reportsSourcing,
    },
  ],
})

const createHiringAnalyticsItem = (t, isAgency, permissions) => ({
  icon: ChartIcon,
  label: t('layout.hiringAnalytics'),
  permission: permissions.reportsSourcing,
  subItems: [
    {
      icon: TriangleIcon,
      label: t('reports.positionCharts'),
      href: '/reports/charts/position',
      permission: permissions.reportsSourcing,
    },
    {
      icon: PersonIcon,
      label: t('reports.candidateCharts'),
      href: '/reports/charts/candidate',
      permission: permissions.reportsSourcing,
    },
    {
      icon: FileIcon,
      label: t('reports.applicationCharts'),
      href: '/reports/charts/application',
      permission: permissions.reportsSourcing,
    },
    {
      icon: MicIcon,
      label: t('reports.interviewCharts'),
      href: '/reports/charts/interview',
      permission: permissions.reportsSourcing,
    },
    {
      icon: StarIcon,
      label: t('reports.requisitionCharts'),
      href: '/reports/charts/requisition',
      permission: permissions.reportsSourcing,
    },
    ...(isAgency
      ? [
          {
            icon: BuildingIcon,
            label: t('reports.agencyCharts'),
            href: '/reports/charts/agency',
            permission: permissions.reportsSourcing,
          },
        ]
      : []),
  ],
})

const createQualityOfHireItem = (t, permissions) => ({
  icon: AwardIcon,
  label: t('reports.qualityOfHire'),
  permission: permissions.reportsSourcing,
  subItems: [
    {
      icon: PercentIcon,
      label: t('reports.interviewToHire.menuLabel'),
      href: '/reports/interview-to-hire',
      permission: permissions.reportsSourcing,
    },
    {
      icon: MoneyIcon,
      label: t('reports.costPerHire.menuLabel'),
      href: '/reports/cost-per-hire',
      permission: permissions.reportsSourcing,
    },
    {
      icon: HourglassIcon,
      label: t('reports.timeToFill'),
      href: '/reports/time-to-fill',
      permission: permissions.reportsSourcing,
    },
    {
      icon: StopwatchIcon,
      label: t('reports.timeToHire'),
      href: '/reports/time-to-hire',
      permission: permissions.reportsSourcing,
    },
  ],
})

const createPerformanceItem = (t, permissions) => ({
  icon: GaugeIcon,
  label: t('reports.performanceReports'),
  permission: permissions.reportsSourcing,
  subItems: [
    {
      icon: UserCheckIcon,
      label: t('reports.hiringManager.menuLabel'),
      href: '/reports/hiring-manager-performance',
      permission: permissions.reportsSourcing,
    },
    {
      icon: UserSearchIcon,
      label: t('reports.recruiter.menuLabel'),
      href: '/reports/recruiter-performance',
      permission: permissions.reportsSourcing,
    },
    {
      icon: PieChartIcon,
      label: t('reports.vacancyRate.menuLabel'),
      href: '/reports/vacancy-rate',
      permission: permissions.reportsSourcing,
    },
    {
      icon: EnvelopeCheckIcon,
      label: t('reports.offerAcceptance.menuLabel'),
      href: '/reports/offer-acceptance-rate',
      permission: permissions.reportsSourcing,
    },
    {
      icon: UserXIcon,
      label: t('reports.rejectionReasons.menuLabel'),
      href: '/reports/rejection-reasons',
      permission: permissions.reportsSourcing,
    },
  ],
})

const createPlanningSection = (t, permissions) => ({
  section: t('layout.planning'),
  items: [
    {
      icon: HierarchyIcon,
      label: t('workforce.workforcePlanning'),
      permission: permissions.reportsSourcing,
      subItems: [
        {
          icon: CalculatorIcon,
          label: t('layout.workforceCalculator'),
          href: '/workforce-structure/calculator',
          permission: permissions.reportsSourcing,
        },
      ],
    },
    {
      icon: WalletIcon,
      label: t('layout.budgetPlanning'),
      href: '/budget-configuration',
      permission: permissions.userReadAll,
    },
  ],
})

const createToolsSection = (t, menuItemsCounter, theme, permissions) => ({
  section: t('layout.tools'),
  items: [
    createCalendarItem(t, permissions),
    createConversationsItem(t, menuItemsCounter, theme),
  ],
})

const createCalendarItem = (t, permissions) => ({
  icon: CalendarIcon,
  label: t('general.calendar'),
  subItems: [
    {
      icon: CalendarIcon,
      label: t('reports.scheduling'),
      href: '/interview-schedule',
      permission: permissions.reportsInterviewSchedule,
    },
    {
      icon: CalendarIcon,
      label: t('reports.personalCalendar'),
      href: '/personal-calendar',
      permission: permissions.userPersonalCalendar,
    },
  ],
})

const createConversationsItem = (t, menuItemsCounter, theme) => ({
  icon: ChatIcon,
  label: t('general.conversations'),
  href: '/conversations',
  count: createCount(menuItemsCounter?.unreadWhatsappCount, 'Unread', theme),
})

const createSettingsSection = (t, permissions) => ({
  section: t('layout.settings'),
  items: [
    createCompanyItem(t, permissions),
    createFormsAndTemplatesItem(t, permissions),
    createHiringDefaultsItem(t, permissions),
    createDataManagementItem(t, permissions),
  ],
})

const createCompanyItem = (t, permissions) => ({
  icon: BuildingIcon,
  label: t('layout.company'),
  subItems: [
    {
      icon: BuildingIcon,
      label: t('pages.companyProfile'),
      href: '/profile',
      permission: permissions.companyRead,
    },
    {
      icon: GearIcon,
      label: t('layout.configuration'),
      href: '/configuration',
      permission: permissions.roleReadAll,
    },
  ],
})

const createFormsAndTemplatesItem = (t, permissions) => ({
  icon: FormIcon,
  label: t('layout.formsAndTemplates'),
  subItems: [
    {
      icon: FormIcon,
      label: t('layout.templates'),
      href: '/system-defaults/templates',
      permission: permissions.jobTemplatesRead,
    },
    {
      icon: FormIcon,
      label: t('layout.referenceForms'),
      href: '/system-defaults/reference-form-generation',
      permission: permissions.userReadAll,
    },
    {
      icon: DatabaseIcon,
      label: t('layout.listsAndOptions'),
      href: '/system-defaults/dropdowns',
      permission: permissions.userReadAll,
    },
  ],
})

const createHiringDefaultsItem = (t, permissions) => ({
  icon: GearIcon,
  label: t('layout.hiringDefaults'),
  permission: permissions.userReadAll,
  subItems: [
    {
      icon: GearIcon,
      label: t('layout.generalDefaultsMenu'),
      href: '/defaults',
      permission: permissions.userReadAll,
    },
    {
      icon: GearIcon,
      label: t('layout.budgets'),
      href: '/budget-configuration',
      permission: permissions.userReadAll,
    },
    {
      icon: GearIcon,
      label: t('layout.scoring'),
      href: '/system-defaults/score-configuration',
      permission: permissions.userReadAll,
    },
  ],
})

const createDataManagementItem = (t, permissions) => ({
  icon: DatabaseIcon,
  label: t('layout.dataManagement'),
  href: '/data-management',
  permission: permissions.dataManagementRead,
})
